import { memo, useCallback } from 'react';
import { TextField, TextFieldProps } from '@nexelem/components';
import { ProLightClock } from '@nexelem/icons/ProLightClock';

type CustomRexExecArray = RegExpExecArray & {
  groups: { hours: string; minutes: string };
};

const execRegularExpression = (value?: string | null): CustomRexExecArray | null => {
  if (!value || !value.includes(':')) {
    return null;
  }

  return /(?<hours>\d\d):(?<minutes>\d?\d)/.exec(value) as CustomRexExecArray | null;
};

export const dateWithDifferenceParser = (value?: string | null) => {
  const result = execRegularExpression(value);

  return {
    hours: result ? Number(result.groups.hours) : 0,
    minutes: result ? Number(result.groups.minutes) : 0,
  };
};

export type InputTimeRangeValue = {
  from: string;
  to: string;
};

export type InputTimeRangeProps = {
  value?: InputTimeRangeValue;
  onChange: (value: InputTimeRangeValue) => void;
  labelFrom?: string;
  labelTo?: string;
  placeholderFrom?: string;
  placeholderTo?: string;
} & Pick<TextFieldProps<string>, 'size' | 'required' | 'enableClearButton' | 'disabled' | 'readOnly'>;

export const InputTimeRange = memo(
  ({
    value = { from: '', to: '' },
    onChange,
    labelFrom,
    labelTo,
    placeholderFrom,
    placeholderTo,
    ...props
  }: InputTimeRangeProps) => {
    const doesChangeValue = useCallback(
      (nextValue: InputTimeRangeValue) => !(nextValue.from === value?.from && nextValue.to === value?.to),
      [value],
    );

    const fromInputOnChange = useCallback(
      (nextFromValue: string) => {
        const nextValue = { ...value, from: nextFromValue };
        doesChangeValue(nextValue) && onChange(nextValue);
      },
      [doesChangeValue, value, onChange],
    );

    const toInputOnChange = useCallback(
      (nextToValue: string) => {
        const nextValue = { ...value, to: nextToValue };
        doesChangeValue(nextValue) && onChange(nextValue);
      },
      [doesChangeValue, value, onChange],
    );

    return (
      <div className="flex w-full items-end gap-1">
        <TextField
          className="flex-1"
          label={labelFrom}
          placeholder={placeholderFrom}
          StartIcon={ProLightClock}
          value={value.from}
          onChange={fromInputOnChange}
          {...props}
          type="time"
        />
        <TextField
          className="flex-1"
          label={labelTo}
          placeholder={placeholderTo}
          type="time"
          value={value.to}
          onChange={toInputOnChange}
          {...props}
        />
      </div>
    );
  },
);

InputTimeRange.displayName = 'TimeInputRange';
